const NAVER_SDK_URL = 'https://static.nid.naver.com/js/naveridlogin_js_sdk_2.0.0.js';
const NAVER_SDK_ID = 'naver-login-sdk';

type NaverUserPayload = { id: string; email: string };

let redirectAttempt: Promise<NaverUserPayload | null> | null = null;
let redirectDelivered = false;

export function getNaverClientId() {
  return (process.env.REACT_APP_NAVER_CLIENT_ID ?? '').replace(/^['"]|['"]$/g, '');
}

/** 네이버 콘솔에 등록된 콜백은 `/auth`. 다른 로그인 화면에서 눌러도 여기로 돌아온다. */
export function getNaverCallbackUrl() {
  const fallback = `${window.location.origin}/auth`;
  const configured = process.env.REACT_APP_CALLBACK_URL;
  if (!configured) return fallback;
  try {
    const url = new URL(configured);
    if (url.origin === window.location.origin) return configured;
  } catch {
    return fallback;
  }
  return fallback;
}

export function hasNaverAccessTokenHash() {
  return typeof window !== 'undefined' && window.location.hash.includes('access_token');
}

export function clearNaverAccessTokenHash() {
  if (!hasNaverAccessTokenHash()) return;
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
}

export function markNaverRedirectDelivered() {
  redirectDelivered = true;
}

export function isNaverRedirectDelivered() {
  return redirectDelivered;
}

function loadNaverSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.naver?.LoginWithNaverId) {
      resolve();
      return;
    }
    const existing = document.getElementById(NAVER_SDK_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('naver sdk')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = NAVER_SDK_ID;
    script.src = NAVER_SDK_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('naver sdk'));
    document.head.appendChild(script);
  });
}

function readNaverUser(user: {
  id?: string;
  email?: string;
  getId?: () => string;
  getEmail?: () => string;
} | null | undefined): NaverUserPayload | null {
  if (!user) return null;
  const id = user.id || user.getId?.();
  const email = user.email || user.getEmail?.() || '';
  if (!id) return null;
  return { id, email };
}

/** `/auth#access_token=` 로 돌아왔을 때 SDK로 프로필을 읽는다. Strict Mode에서도 1회만 실행. */
export function completeNaverRedirectLogin(): Promise<NaverUserPayload | null> {
  if (redirectAttempt) return redirectAttempt;

  redirectAttempt = (async () => {
    if (!hasNaverAccessTokenHash()) return null;
    await loadNaverSdk();
    const naver = window.naver;
    if (!naver?.LoginWithNaverId) return null;

    const naverLogin = new naver.LoginWithNaverId({
      clientId: getNaverClientId(),
      callbackUrl: getNaverCallbackUrl(),
      isPopup: false,
      callbackHandle: true,
    });
    naverLogin.init();

    return await new Promise<NaverUserPayload | null>((resolve) => {
      naverLogin.getLoginStatus((status: boolean) => {
        if (!status) {
          resolve(null);
          return;
        }
        resolve(readNaverUser(naverLogin.user));
      });
    });
  })().catch((error) => {
    console.error('completeNaverRedirectLogin', error);
    return null;
  });

  return redirectAttempt;
}

declare global {
  interface Window {
    naver?: {
      LoginWithNaverId: new (options: {
        clientId: string;
        callbackUrl: string;
        isPopup?: boolean;
        callbackHandle?: boolean;
        loginButton?: { color: string; type: number; height: number };
      }) => {
        init: () => void;
        getLoginStatus: (cb: (status: boolean) => void) => void;
        user?: {
          id?: string;
          email?: string;
          getId?: () => string;
          getEmail?: () => string;
        };
      };
    };
  }
}
